import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { find } from 'lodash-es'
import { useSceneLineMarker } from '@editor/hooks'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useModelStore } from '@editorStores'
import {
  buildPreviewWallShapePoints,
  buildWallShapeOutlineLines,
  hasWallFormGeometryChanges,
} from './utils'

interface Props {
  wallGuid: string
}

const toImmutableVector3 = (
  value: { x: number; y: number; z: number } | undefined,
): ImmutableVector3 | null => {
  if (
    typeof value?.x !== 'number' ||
    typeof value?.y !== 'number' ||
    typeof value?.z !== 'number'
  ) {
    return null
  }

  return new ImmutableVector3(value.x, value.y, value.z)
}

const WallFormSceneMarker = ({ wallGuid }: Props): null => {
  const originalShapePoints = useModelStore(
    state => find(state.model.walls, { guid: wallGuid })?.shape.points,
  )
  const start = useWatch({ name: 'start' })
  const end = useWatch({ name: 'end' })

  const { outlineLines, startEdgeLine, endEdgeLine, hasChanges } = useMemo(() => {
    const startVector = toImmutableVector3(start)
    const endVector = toImmutableVector3(end)

    if (!originalShapePoints || !startVector || !endVector) {
      return { outlineLines: [], startEdgeLine: null, endEdgeLine: null, hasChanges: false }
    }

    const previewPoints = buildPreviewWallShapePoints(originalShapePoints, startVector, endVector)
    const lines = buildWallShapeOutlineLines(previewPoints)
    const hasChanges = hasWallFormGeometryChanges(originalShapePoints, startVector, endVector)

    return {
      outlineLines: hasChanges ? lines.outlineLines : [],
      startEdgeLine: lines.startEdgeLine,
      endEdgeLine: lines.endEdgeLine,
      hasChanges,
    }
  }, [end, originalShapePoints, start])

  useSceneLineMarker({
    lines: outlineLines,
    enabled: hasChanges && outlineLines.length > 0,
    color: 'black',
    lineWidth: 5,
    renderOrder: 999,
    disableDepthTest: true,
  })

  useSceneLineMarker({
    lines: startEdgeLine ? [startEdgeLine] : [],
    enabled: startEdgeLine !== null,
    color: 'red',
    lineWidth: 5,
    renderOrder: 1000,
    disableDepthTest: true,
  })

  useSceneLineMarker({
    lines: endEdgeLine ? [endEdgeLine] : [],
    enabled: endEdgeLine !== null,
    color: 'blue',
    lineWidth: 5,
    renderOrder: 1000,
    disableDepthTest: true,
  })

  return null
}

export default WallFormSceneMarker
